import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Calendar, CheckSquare, Check, ChevronRight, Clock, Loader2, Plus, Users } from 'lucide-react';
import useDisplayZone from '../../hooks/useDisplayZone';
import { useTasks, useCreateTask, useCompleteTask, useReopenTask } from '../../hooks/useTasks';
import { dueState, dueText, dueTone, formatDue, isTaskClosed, viewerTodayKey } from '../../lib/taskDue';

/**
 * The Inbox's Tasks view (moved out of pages/Inbox.jsx so it can be tested alone): the viewer's overdue and
 * due-today tasks with a quick-add for today. Both the groups and each row's label come from the viewer's own
 * day (`dueState`, the rule every task surface shares), never from the server's `date_group`, so a row cannot
 * sit under "Today" carrying another day's label.
 */
export default function InboxTasksTab() {
    const navigate = useNavigate();
    const zone = useDisplayZone();
    const { data: tasks = [], isLoading } = useTasks({ status: 'open', sort_by: 'due_date', sort_dir: 'asc' });
    const createTask = useCreateTask();
    const completeTask = useCompleteTask();
    const reopenTask = useReopenTask();
    const [quickTitle, setQuickTitle] = useState('');

    // Only show overdue + today tasks.
    const focusedTasks = tasks.filter(t => ['overdue', 'today'].includes(dueState(t.due_date, zone)));
    const overdueTasks = focusedTasks.filter(t => dueState(t.due_date, zone) === 'overdue');
    const todayTasks = focusedTasks.filter(t => dueState(t.due_date, zone) === 'today');

    const handleQuickAdd = (e) => {
        e.preventDefault();
        if (!quickTitle.trim()) return;
        const today = viewerTodayKey(zone);
        createTask.mutate({
            title: quickTitle.trim(),
            due_date: today,
            priority: 'medium',
            source: 'manual',
        }, {
            onSuccess: () => { setQuickTitle(''); toast.success('Task created'); },
            onError: () => toast.error('Failed to create task'),
        });
    };

    const handleToggle = (task) => {
        if (isTaskClosed(task)) reopenTask.mutate(task.activity_id);
        else completeTask.mutate(task.activity_id);
    };

    const TaskItem = ({ task }) => {
        const isCompleted = isTaskClosed(task);
        const state = isCompleted ? 'closed' : dueState(task.due_date, zone);
        return (
            <div className="flex items-center px-4 py-2.5 hover:bg-stone-50 border-b border-stone-100 transition-colors">
                <button
                    onClick={() => handleToggle(task)}
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mr-3 shrink-0 transition-colors ${
                        isCompleted
                            ? 'bg-emerald-500 border-emerald-500 text-white'
                            : 'border-stone-300 hover:border-stone-500'
                    }`}
                >
                    {isCompleted && <Check className="w-3 h-3" />}
                </button>
                <div className="flex-1 min-w-0 mr-3">
                    <span className={`text-sm ${isCompleted ? 'line-through text-stone-400' : 'text-stone-900'}`}>
                        {task.title}
                    </span>
                    {task.contact_name && (
                        <button
                            onClick={() => navigate(`/contacts/${task.contact_id}`)}
                            className="flex items-center gap-1 text-[11px] text-stone-400 hover:text-blue-600 mt-0.5"
                        >
                            <Users className="w-3 h-3" /> {task.contact_name}
                        </button>
                    )}
                </div>
                {task.due_date && (
                    <span className={`text-xs shrink-0 ${dueTone(state)}`} data-testid="inbox-task-due">
                        {state === 'overdue' ? dueText(task.due_date, 'overdue', zone) : formatDue(task.due_date, zone)}
                    </span>
                )}
            </div>
        );
    };

    return (
        <div className="flex-1 overflow-y-auto">
            {/* Quick add */}
            <form onSubmit={handleQuickAdd} className="px-4 py-3 border-b border-stone-100">
                <div className="flex items-center gap-2">
                    <Plus className="w-4 h-4 text-stone-400 shrink-0" />
                    <input
                        value={quickTitle}
                        onChange={(e) => setQuickTitle(e.target.value)}
                        placeholder="Quick add task for today..."
                        className="flex-1 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none bg-transparent"
                    />
                    {quickTitle.trim() && (
                        <button type="submit" disabled={createTask.isPending}
                            className="px-3 py-1.5 text-xs font-medium text-white bg-stone-900 rounded-lg hover:bg-stone-800 disabled:opacity-50">
                            {createTask.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Add'}
                        </button>
                    )}
                </div>
            </form>

            {isLoading ? (
                <div className="flex items-center justify-center h-40">
                    <Loader2 className="w-5 h-5 animate-spin text-stone-400" />
                </div>
            ) : focusedTasks.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-stone-400">
                    <CheckSquare className="w-10 h-10 mb-2 opacity-30" />
                    <p className="text-sm font-medium text-stone-500">All caught up!</p>
                    <p className="text-xs mt-1">No overdue or today tasks</p>
                </div>
            ) : (
                <>
                    {overdueTasks.length > 0 && (
                        <div>
                            <div className="px-4 py-2 bg-red-50 border-b border-red-200 flex items-center gap-2">
                                <Clock className="w-3.5 h-3.5 text-red-600" />
                                <span className="text-xs font-semibold text-red-600">Overdue ({overdueTasks.length})</span>
                            </div>
                            {overdueTasks.map(t => <TaskItem key={t.activity_id} task={t} />)}
                        </div>
                    )}
                    {todayTasks.length > 0 && (
                        <div>
                            <div className="px-4 py-2 bg-blue-50 border-b border-blue-200 flex items-center gap-2">
                                <Calendar className="w-3.5 h-3.5 text-blue-600" />
                                <span className="text-xs font-semibold text-blue-600">Today ({todayTasks.length})</span>
                            </div>
                            {todayTasks.map(t => <TaskItem key={t.activity_id} task={t} />)}
                        </div>
                    )}
                </>
            )}

            {/* View All link */}
            <div className="px-4 py-3 border-t border-stone-100">
                <button
                    onClick={() => navigate('/tasks')}
                    className="text-xs font-medium text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                >
                    View All Tasks <ChevronRight className="w-3 h-3" />
                </button>
            </div>
        </div>
    );
}
